import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AuthGateContext } from '@/auth-gate';
import { useCompany } from '@/company/context';
import { isSettingsView, parseWorkspaceRoute } from '@/workspace/navigation';
import { fetchBilling, hasPaidAccess, type BillingStatus } from './api';

export function useBillingState(
  required: boolean | null,
  userId: string | null,
  timezone: string | null = null,
) {
  const [record, setRecord] = useState<{ userId: string; status: BillingStatus } | null>(null);
  const [error, setError] = useState(false);
  // A server refusal (reject) holds the gate closed until the next read answers; never reopened by an unknown status.
  const [refused, setRefused] = useState(false);
  // The last status really read for each person survives a re-read, a re-bootstrap or one failed poll.
  const good = useRef<{ userId: string; status: BillingStatus } | null>(null);
  const [open, setOpen] = useState(false);
  const [editActive, setEditActive] = useState(false);
  const activeEdits = useRef(0);
  const registerEditDraft = useCallback(() => {
    activeEdits.current += 1;
    setEditActive(true);
    return () => {
      activeEdits.current -= 1;
      setEditActive(activeEdits.current > 0);
    };
  }, []);
  const [returnState, setReturnState] = useState(() => {
    const params = new URLSearchParams(location.search);
    const result = params.get('billing');
    if (result) {
      params.delete('billing');
      history.replaceState(
        null,
        '',
        location.pathname + (params.size ? `?${params}` : '') + location.hash,
      );
    }
    return result === 'success'
      ? 'pending'
      : result === 'cancelled' || result === 'canceled' || result === 'cancel'
        ? 'canceled'
        : '';
  });
  const confirmingCheckout = useRef(returnState === 'pending');
  const identity = useRef(userId);
  identity.current = userId;
  const status = record?.userId === userId ? record.status : null;
  const refresh = useCallback(async () => {
    if (required !== true || !userId) return;
    try {
      const next = await fetchBilling();
      if (identity.current !== userId) return;
      good.current = { userId, status: next };
      setRecord({ userId, status: next });
      setRefused(false);
      setError(!next.enabled);
      // A paid Checkout shows up as an active plan once Stripe confirms it.
      const confirmed = next.account?.planActive === true;
      if (confirmed) {
        if (confirmingCheckout.current) setOpen(false);
        confirmingCheckout.current = false;
        setReturnState('');
      }
    } catch {
      if (identity.current !== userId) return;
      // One failed read does not turn a known answer into "refused"; it only matters when nothing is known.
      if (good.current?.userId === userId) return;
      setRecord(null);
      setError(true);
    }
  }, [required, userId]);
  useEffect(() => {
    // A re-read keeps the status already known for this person; only an unknown one starts unknown.
    if (good.current?.userId !== userId) setRecord(null);
    setError(false);
    void refresh();
    if (required !== true || !userId) return;
    const timer = setInterval(() => void refresh(), 30000);
    const focus = () => {
      void refresh();
    };
    window.addEventListener('focus', focus);
    document.addEventListener('visibilitychange', focus);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', focus);
      document.removeEventListener('visibilitychange', focus);
    };
  }, [refresh, required, userId]);
  useEffect(() => {
    if (returnState !== 'pending' || required !== true || !userId) return;
    // Checkout lands in chat. Retain inline handling for older settings returns.
    if (!isSettingsView(parseWorkspaceRoute(location.hash).view)) setOpen(true);
    const timer = setInterval(() => void refresh(), 2000);
    const end = setTimeout(() => {
      clearInterval(timer);
      setReturnState('delayed');
    }, 30000);
    return () => {
      clearInterval(timer);
      clearTimeout(end);
    };
  }, [returnState, required, userId, refresh]);
  // "Not known yet" (a read in flight) is not "payment required": the server stays the authority and answers a real
  // refusal itself. Only a read that FAILED with nothing known, a billing-disabled answer, a server refusal or a
  // known empty pool closes the gate.
  const loading = required === true && status === null && !error && !refused;
  const allowed = () =>
    required === false || (required === true && (loading || (!error && hasPaidAccess(status))));
  const canAsk = allowed();
  return {
    editActive,
    registerEditDraft,
    hasActiveEdit: () => activeEdits.current > 0,
    timezone,
    required,
    status,
    error: error || required === null,
    canAsk,
    open,
    setOpen,
    refresh,
    returnState,
    request: () => {
      setOpen(true);
      void refresh();
    },
    check: allowed,
    reject: () => {
      setRefused(true);
      setRecord(null);
      good.current = null;
      setOpen(true);
      void refresh();
    },
  };
}
export type BillingState = ReturnType<typeof useBillingState> & {
  /** Saves the composer draft before a Stripe redirect (set by the app shell). */
  beforeRedirect?: () => void;
};
export const BillingContext = createContext<BillingState | null>(null);
export const useBilling = () => useContext(BillingContext);

// Historical-message edits belong to their message composer, not the bottom
// composer whose text survives checkout. Keep them mounted until the user
// explicitly copies/cancels; stable registration handles multiple edits too.
export function useBillingEditDraftGuard() {
  const registerEditDraft = useBilling()?.registerEditDraft;
  useEffect(() => registerEditDraft?.(), [registerEditDraft]);
}

// All composer, suggestion, edit and retry paths share the same ordering.
export function useQuestionGate() {
  const auth = useContext(AuthGateContext);
  const billing = useBilling();
  const company = useCompany();
  const companyBlocked = !company.ready;
  return {
    needsAuth: auth.needsAuth || companyBlocked || Boolean(billing && !billing.canAsk),
    requestAuth: auth.needsAuth
      ? auth.requestAuth
      : companyBlocked
        ? () => {}
        : () => billing?.request(),
    allow: () => {
      if (auth.needsAuth) {
        auth.requestAuth();
        return false;
      }
      if (companyBlocked) return false;
      if (billing && !billing.check()) {
        billing.request();
        return false;
      }
      return true;
    },
  };
}
